/*
 * Scentt rails \u2013 recently viewed, You may also love (similar scents), the wishlist page grid and
 * Wear it with. Cards come from the Section Rendering endpoint ?section_id=scentt-card on a product URL.
 *
 * <scentt-rail-cards data-source="recent|similar|wishlist" data-max data-current data-record
 *   data-ratio data-hover data-show-vendor data-buttons>  \u2026 <ul data-scentt-cards-list> \u2026
 * Delegated: form[data-scentt-card-form] (Add to bag on cards), [data-scentt-add-both] (Wear it with).
 */
(() => {
  const start = () => {
    const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
    if (!S || customElements.get('scentt-rail-cards')) return;

    const RECENT_MAX = 20;
    const recent = S.list('scentt:recent', RECENT_MAX);
    const wishlist = S.list('scentt:wishlist', 100);
    const RATIOS = { square: 100, portrait: 125, tall: 150, landscape: 75 };

    /* While previewing an unpublished theme, ask Shopify for this theme's sections, not the live theme's. */
    const preview = (sep) => {
      const t = window.Shopify && window.Shopify.theme;
      return t && t.role && t.role !== 'main' && S.isId(t.id) ? `${sep}preview_theme_id=${t.id}` : '';
    };

    /* ---------- cards (one request per handle, cached for the page) ---------- */
    const cards = new Map();
    const fetchCard = (handle) => {
      if (!cards.has(handle)) {
        const p = S.fetchSection(`/products/${encodeURIComponent(handle)}${preview('?')}`, 'scentt-card').then((doc) => {
          const item = doc.querySelector('.scentt-card-item');
          if (!item) throw new S.Error('no card', { status: 404 });
          return item;
        });
        p.catch(() => cards.delete(handle));
        cards.set(handle, p);
      }
      return cards.get(handle);
    };

    /* ---------- product index for similar scents (cached in the session for 10 minutes) ---------- */
    const isItem = (x) => x && typeof x === 'object' && S.isHandle(x.h) && Array.isArray(x.t);
    const cleanItem = (x) => ({
      h: x.h,
      v: typeof x.v === 'string' ? x.v.slice(0, 120) : '',
      t: x.t.filter((t) => typeof t === 'string').slice(0, 250).map((t) => t.slice(0, 80)),
      n: typeof x.n === 'string' ? x.n.slice(0, 1000) : '',
      a: x.a !== false,
    });
    const indexes = new Map();
    const loadIndex = (path) => {
      if (indexes.has(path)) return indexes.get(path);
      const key = `scentt:index:${path}`;
      try {
        const cached = JSON.parse(window.sessionStorage.getItem(key) || 'null'); /* inside try */
        if (cached && Date.now() - cached.at < 600000 && Array.isArray(cached.items)) {
          const items = cached.items.filter(isItem).map(cleanItem);
          const ready = Promise.resolve(items);
          indexes.set(path, ready);
          return ready;
        }
      } catch (e) { /* storage blocked or malformed: fetch again */ }
      const sep = path.includes('?') ? '&' : '?';
      const page = (n) => S.fetchSection(`${path}${sep}page=${n}${preview('&')}`, 'scentt-similar').then((doc) => {
        const el = doc.querySelector('[data-scentt-index]');
        if (!el) throw new S.Error('no index');
        let items = [];
        try { items = JSON.parse(el.getAttribute('data-items') || '[]'); } catch (e) { items = []; }
        return { items: Array.isArray(items) ? items.filter(isItem).map(cleanItem) : [], pages: S.int(el.getAttribute('data-pages'), 1, 1, 8) };
      });
      const ready = page(1).then(async (first) => {
        const rest = [];
        for (let n = 2; n <= first.pages; n += 1) rest.push(page(n));
        const more = await Promise.allSettled(rest);
        const items = more.reduce((all, r) => (r.status === 'fulfilled' ? all.concat(r.value.items) : all), first.items);
        try { window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), items })); } catch (e) { /* full or blocked */ }
        return items;
      });
      ready.catch(() => indexes.delete(path));
      indexes.set(path, ready);
      return ready;
    };

    const norm = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
    const GENDER = ['for him', 'for her', 'unisex'];
    const FAMILY = ['woody', 'floral', 'fresh', 'amber', 'gourmand', 'oud', 'citrus', 'chypre', 'fougere', 'leather'];
    const LIGHT = ['day', 'evening', 'occasion', 'spicy', 'sweet', 'fruity', 'aromatic', 'musk', 'powdery', 'aquatic', 'green', 'spring', 'summer', 'autumn', 'winter'];
    const SKIP = ['gift set', 'discovery set', 'coming soon', 'newsletter', 'new', 'sale', 'bestseller'];
    const HIDE = ['sample', 'free sample', 'gift wrap', 'hidden'];
    const SETS = ['gift set', 'discovery set'];
    const weight = (t) => {
      if (GENDER.includes(t) || SKIP.includes(t)) return 0;
      if (FAMILY.includes(t)) return 2;
      if (LIGHT.includes(t)) return 0.75;
      return 1.5; /* notes and styles: oud, vanilla, rose, foug\u00e8re\u2026 */
    };

    /** Pure scoring \u2013 exported on the element for tests. */
    const pickSimilar = (items, { handle, tags, notes, brand, max, perBrand }) => {
      const mine = new Set([...tags.map(norm), ...notes.map(norm)].filter(Boolean));
      const isSet = SETS.some((t) => mine.has(t));
      const him = mine.has('for him');
      const her = mine.has('for her');
      const need = him && !her ? 'for him' : her && !him ? 'for her' : '';
      const vendor = norm(brand);
      const scored = [];
      items.forEach((p) => {
        if (p.h === handle) return;
        const theirs = new Set([...p.t.map(norm), ...p.n.split('|').map(norm)].filter(Boolean));
        if (HIDE.some((t) => theirs.has(t))) return;
        if (need && !theirs.has(need) && !theirs.has('unisex')) return;
        const theirSet = SETS.some((t) => theirs.has(t));
        if (theirSet !== isSet) return;
        let score = 0;
        theirs.forEach((t) => { if (mine.has(t)) score += weight(t); });
        if (score <= 0) return;
        if (!p.a) score -= 3;
        if (theirs.has('coming soon')) score -= 5;
        if (norm(p.v) === vendor) score -= 0.5; /* gently favour other houses */
        scored.push({ h: p.h, v: norm(p.v), s: score, r: Math.random() });
      });
      scored.sort((a, b) => b.s - a.s || a.r - b.r);
      const pick = [];
      const count = {};
      for (let i = 0; i < scored.length && pick.length < max; i += 1) {
        const m = scored[i];
        if ((count[m.v] || 0) < perBrand) {
          count[m.v] = (count[m.v] || 0) + 1;
          pick.push(m.h);
        }
      }
      return pick;
    };

    class ScenttRailCards extends HTMLElement {
      connectedCallback() {
        this.source = S.oneOf(this.dataset.source, ['recent', 'similar', 'wishlist', 'shared'], 'recent');
        this.max = S.int(this.dataset.max, 4, 1, 48);
        this.list = this.querySelector('[data-scentt-cards-list]');
        this.empty = this.querySelector('[data-scentt-cards-empty]');
        this.current = S.isHandle(this.dataset.current) ? this.dataset.current : '';
        this.cleanups = [];
        if (!this.list) return;
        if (this.source === 'recent' && this.dataset.record === 'true' && this.current) recent.add(this.current);
        if (this.source === 'wishlist') {
          const rerender = S.debounce(() => this.load(), 60);
          this.cleanups.push(wishlist.onChange(rerender));
          this.load();
        } else if (this.source === 'shared') {
          /* loaded on demand by <scentt-wishlist> when a shared ?wl= link is opened */
        } else {
          this.cleanups.push(S.inView(this, () => this.load(), '400px'));
        }
      }

      disconnectedCallback() {
        (this.cleanups || []).forEach((fn) => typeof fn === 'function' && fn());
        this.cleanups = [];
      }

      handles() {
        if (this.source === 'wishlist') return Promise.resolve(wishlist.all().slice(0, this.max));
        if (this.source === 'shared') {
          const list = String(this.dataset.handles || '').split(',').filter((h) => S.isHandle(h));
          return Promise.resolve(list.filter((h, i) => list.indexOf(h) === i).slice(0, this.max));
        }
        if (this.source === 'recent') return Promise.resolve(recent.all().filter((h) => h !== this.current).slice(0, this.max));
        let tags = [];
        try { tags = JSON.parse(this.dataset.tags || '[]'); } catch (e) { tags = []; } /* try: malformed data-tags */
        tags = Array.isArray(tags) ? tags.filter((t) => typeof t === 'string').slice(0, 250) : [];
        const notes = String(this.dataset.notes || '').slice(0, 1000).split('|');
        const path = String(this.dataset.index || '');
        if (!/^\/(?!\/)[\w\-./%]*$/.test(path)) return Promise.resolve([]);
        return loadIndex(path).then((items) => pickSimilar(items, {
          handle: this.current,
          tags,
          notes,
          brand: this.dataset.brand,
          max: this.max,
          perBrand: S.int(this.dataset.perBrand, 2, 1, 12),
        }));
      }

      async load() {
        const token = (this.token || 0) + 1;
        this.token = token;
        let handles = [];
        try {
          handles = await this.handles();
        } catch (e) {
          S.warn('rail handles', e);
          handles = [];
        }
        if (token !== this.token) return;
        if (!handles.length) { this.finish(0, 0); return; }
        this.hidden = false;
        /* at most 6 requests at a time: avoids Shopify rate limits and time-outs with long lists */
        const results = new Array(handles.length);
        let next = 0;
        const worker = async () => {
          while (next < handles.length) {
            const i = next;
            next += 1;
            try { results[i] = { status: 'fulfilled', value: await fetchCard(handles[i]) }; } catch (e) { results[i] = { status: 'rejected', reason: e }; }
          }
        };
        await Promise.all(Array.from({ length: Math.min(6, handles.length) }, worker));
        if (token !== this.token || !this.isConnected) return;
        const nodes = [];
        let failed = 0;
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') {
            const node = document.importNode(r.value, true);
            node.setAttribute('data-handle', handles[i]);
            this.decorate(node);
            nodes.push(node);
          } else if (r.reason && r.reason.status === 404) {
            if (this.source === 'recent') recent.remove(handles[i]); /* product deleted or unpublished */
          } else {
            failed += 1;
          }
        });
        this.list.replaceChildren(...nodes);
        this.list.scrollLeft = 0;
        this.list.dispatchEvent(new Event('scroll'));
        this.finish(nodes.length, failed);
        S.emit('cards:render', { source: this.source, count: nodes.length });
      }

      finish(count, failed) {
        if (this.source === 'wishlist') {
          if (!count) this.list.replaceChildren();
          this.list.hidden = count === 0;
          if (this.empty) this.empty.hidden = count > 0;
          if (failed && !count) S.toast(this.dataset.error || S.config.strings.error, 'error');
          return;
        }
        if (!count) {
          this.list.replaceChildren();
          this.hidden = !(window.Shopify && window.Shopify.designMode);
        }
      }

      decorate(node) {
        if (this.dataset.hover !== 'true') {
          node.querySelectorAll('.media--hover-effect').forEach((m) => {
            m.classList.remove('media--hover-effect');
            m.querySelectorAll('img').forEach((img, i) => { if (i > 0) img.remove(); });
          });
        }
        if (this.dataset.showVendor !== 'true') {
          node.querySelectorAll('.card-information .caption-with-letter-spacing, .card__information .caption-with-letter-spacing').forEach((el) => {
            const prev = el.previousElementSibling;
            if (prev && prev.classList.contains('visually-hidden')) prev.remove();
            el.remove();
          });
        }
        if (this.dataset.buttons === 'false') node.querySelectorAll('.scentt-card-item__actions').forEach((el) => el.remove());
        const mode = S.oneOf(this.dataset.ratio, ['adapt', 'square', 'portrait', 'tall', 'landscape'], 'square');
        node.querySelectorAll('.card').forEach((card) => {
          let pct = RATIOS[mode];
          if (mode === 'adapt') {
            const img = card.querySelector('.card__media img');
            const w = img ? Number(img.getAttribute('width')) : 0;
            const h = img ? Number(img.getAttribute('height')) : 0;
            pct = w > 0 && h > 0 ? Math.round((h / w) * 1000) / 10 : 100;
          }
          [card, card.querySelector('.card__inner')].forEach((el) => { if (el) el.style.setProperty('--ratio-percent', `${pct}%`); });
        });
      }
    }
    ScenttRailCards.pickSimilar = pickSimilar;
    customElements.define('scentt-rail-cards', ScenttRailCards);

    /* ---------- Add to bag under cards ----------
       <scentt-card-add data-added="Added"><form data-scentt-card-form>\u2026</form></scentt-card-add>
       Progressive: without JS (or before this file loads) the form posts to /cart/add natively. */
    const busy = (btn, on) => {
      if (!btn) return;
      btn.classList.toggle('loading', on);
      btn.setAttribute('aria-busy', on ? 'true' : 'false');
      btn.disabled = on;
    };
    class ScenttCardAdd extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.onSubmit = this.onSubmit.bind(this);
        if (this.form) this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this.onSubmit);
        clearTimeout(this.timer);
      }

      onSubmit(e) {
        e.preventDefault();
        const input = this.form.querySelector('input[name="id"]');
        const btn = this.form.querySelector('[type="submit"]');
        if (!input || !S.isId(input.value) || !btn || btn.disabled) return;
        const label = btn.querySelector('[data-scentt-card-label]') || btn;
        if (!this.original) this.original = label.textContent;
        busy(btn, true);
        return S.cart.add([{ id: input.value, quantity: 1 }])
          .then(() => {
            busy(btn, false);
            btn.classList.add('is-added');
            label.textContent = this.dataset.added || this.original;
            clearTimeout(this.timer);
            this.timer = setTimeout(() => { btn.classList.remove('is-added'); label.textContent = this.original; }, 2500);
          })
          .catch(() => { busy(btn, false); /* Scentt.cart already showed a human message */ });
      }
    }
    if (!customElements.get('scentt-card-add')) customElements.define('scentt-card-add', ScenttCardAdd);

    /* ---------- Wear it with: add both ---------- */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-scentt-add-both]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      const ids = String(btn.getAttribute('data-scentt-add-both') || '').split(',').filter(S.isId).slice(0, 2);
      if (!ids.length) return;
      busy(btn, true);
      S.cart.add(ids.map((id) => ({ id, quantity: 1 })))
        .catch(() => { /* toast shown by Scentt.cart */ })
        .finally(() => busy(btn, false));
    });
  };

  if (window.Scentt) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
